"use client";

import { useEffect, useState } from "react";

export type HeroReportPanel = {
	code: string;
	label: string;
	value: string;
	detail: string;
	stats: { label: string; value: string }[];
};

interface HeroReportCardProps {
	topPanels: HeroReportPanel[];
	bottomPanels: HeroReportPanel[];
	panelLabels: [string, string];
	intervalMs?: number;
}

function useCycler<T>(items: T[], intervalMs: number, paused: boolean) {
	const [idx, setIdx] = useState(0);
	useEffect(() => {
		if (paused || items.length < 2) return;
		if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
		const id = setInterval(() => setIdx((i) => (i + 1) % items.length), intervalMs);
		return () => clearInterval(id);
	}, [paused, items.length, intervalMs]);
	return [idx, setIdx, items[idx]] as const;
}

export function HeroReportCard({
	topPanels,
	bottomPanels,
	panelLabels,
	intervalMs = 4000,
}: HeroReportCardProps) {
	const [paused, setPaused] = useState(false);
	const [topIdx, setTopIdx, topPanel] = useCycler(topPanels, intervalMs, paused);
	const [bottomIdx, setBottomIdx, bottomPanel] = useCycler(bottomPanels, intervalMs, paused);
	if (!topPanel || !bottomPanel) return null;

	return (
		<div
			className="relative flex h-full min-h-[220px] w-full flex-col gap-2 sm:min-h-[300px] sm:gap-3 lg:min-h-[460px] lg:gap-4"
			onMouseEnter={() => setPaused(true)}
			onMouseLeave={() => setPaused(false)}
		>
			<ReportPanel
				panel={topPanel}
				panelIdx={topIdx}
				panels={topPanels}
				onSelect={setTopIdx}
				panelLabel={panelLabels[0]}
			/>
			<ReportPanel
				panel={bottomPanel}
				panelIdx={bottomIdx}
				panels={bottomPanels}
				onSelect={setBottomIdx}
				panelLabel={panelLabels[1]}
			/>
		</div>
	);
}

function ReportPanel({
	panel,
	panelIdx,
	panels,
	onSelect,
	panelLabel,
}: {
	panel: HeroReportPanel;
	panelIdx: number;
	panels: HeroReportPanel[];
	onSelect: (index: number) => void;
	panelLabel: string;
}) {
	return (
		<div className="relative flex-1 overflow-hidden rounded-2xl border border-white/15 bg-primary-dark text-white shadow-[0_18px_40px_-20px_rgba(8,39,74,0.45)]">
			<div
				aria-hidden
				className="absolute inset-0 opacity-[0.1]"
				style={{
					backgroundImage:
						"linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)",
					backgroundSize: "32px 32px",
				}}
			/>
			<Corner className="left-3 top-3" />
			<Corner className="right-3 top-3 rotate-90" />
			<Corner className="bottom-3 left-3 -rotate-90" />
			<Corner className="bottom-3 right-3 rotate-180" />
			<div className="absolute inset-x-0 top-0 flex items-start justify-between px-3 pt-2.5 font-mono text-[0.48rem] uppercase tracking-[0.16em] sm:text-[0.54rem] lg:px-5 lg:pt-4 lg:text-[0.6rem] lg:tracking-[0.2em]">
				<div className="flex flex-col gap-0.5 lg:gap-1">
					<span className="hidden text-white/60 sm:inline">{panelLabel}</span>
					<span className="flex items-center gap-1.5 text-white lg:gap-2">
						<span className="block h-1 w-1 rounded-full bg-white lg:h-1.5 lg:w-1.5" />
						{panel.code}
					</span>
				</div>
				<div className="flex gap-1 lg:gap-1.5">
					{panels.map((item, index) => (
						<button
							key={item.code}
							type="button"
							onClick={() => onSelect(index)}
							aria-label={`${panelLabel} ${index + 1}`}
							className={`h-px transition-all duration-500 ${index === panelIdx ? "h-[2px] w-4 bg-white lg:w-6" : "w-1.5 bg-white/40 hover:bg-white/70 lg:w-2"}`}
						/>
					))}
				</div>
			</div>
			<div className="relative flex h-full flex-col px-3 pb-2.5 pt-[2.6rem] sm:px-4 sm:pb-3 sm:pt-[3.2rem] lg:px-5 lg:pb-4 lg:pt-[4.5rem]">
				{panels.map((item, index) => (
					<div
						key={item.code}
						aria-hidden={index !== panelIdx}
						className={`absolute inset-0 flex flex-col px-3 pb-2.5 pt-[2.6rem] transition-opacity duration-700 ease-out sm:px-4 sm:pb-3 sm:pt-[3.2rem] lg:px-5 lg:pb-4 lg:pt-[4.5rem] ${index === panelIdx ? "opacity-100" : "pointer-events-none opacity-0"}`}
					>
						<div className="truncate font-mono text-[0.5rem] uppercase tracking-[0.16em] text-white/75 sm:text-[0.56rem] lg:text-[0.62rem] lg:tracking-[0.2em]">
							{item.label}
						</div>
						<div className="mt-1 font-display text-[1.2rem] font-medium leading-none tracking-[-0.018em] sm:text-[1.6rem] lg:text-[clamp(1.7rem,3vw,2.5rem)]">
							{item.value}
						</div>
						<DocumentGraph />
						<div className="mt-2 flex items-center gap-1.5 font-mono text-[0.46rem] uppercase tracking-[0.14em] text-white/85 sm:text-[0.5rem] lg:mt-3 lg:text-[0.55rem] lg:tracking-[0.18em]">
							<span className="h-2 w-2 rounded-full border border-current" />
							{item.detail}
						</div>
						<div className="mt-3 hidden border-t border-white/15 pt-3 lg:block">
							<dl className="grid grid-cols-3 gap-2 text-[0.65rem]">
								{item.stats.map((stat) => (
									<div key={stat.label} className="min-w-0">
										<dt className="font-mono text-[0.5rem] uppercase tracking-[0.14em] text-white/60">
											{stat.label}
										</dt>
										<dd className="mt-0.5 truncate font-mono text-white">{stat.value}</dd>
									</div>
								))}
							</dl>
						</div>
					</div>
				))}
			</div>
		</div>
	);
}

function DocumentGraph() {
	return (
		<svg
			aria-hidden
			viewBox="0 0 280 60"
			className="mt-2 h-full max-h-[64px] w-full flex-1 text-white"
			preserveAspectRatio="none"
		>
			<path
				d="M0 48 H280 M0 30 H280 M0 12 H280 M20 55 V5 M92 55 V5 M164 55 V5 M236 55 V5"
				stroke="currentColor"
				strokeOpacity="0.22"
				strokeWidth="0.6"
			/>
			<path d="M18 40 H112 L132 20 H262" fill="none" stroke="currentColor" strokeWidth="1.4" />
			<circle cx="132" cy="20" r="3" fill="currentColor" />
		</svg>
	);
}

function Corner({ className = "" }: { className?: string }) {
	return (
		<svg
			aria-hidden
			viewBox="0 0 12 12"
			className={`pointer-events-none absolute h-3 w-3 text-white/35 ${className}`}
			fill="none"
			stroke="currentColor"
			strokeWidth="1"
		>
			<path d="M0 4 V0 H4" />
		</svg>
	);
}
